import { Modal, Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Paper, IconButton, Collapse, useTheme, Tooltip, TextField, Grid } from "@mui/material";
import { Order, OrderState } from "../../models/order";
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useState, useEffect } from "react";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PendingIcon from '@mui/icons-material/Pending';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import VerifiedIcon from '@mui/icons-material/Verified';
import PostAddIcon from '@mui/icons-material/PostAdd';

interface Props {
    open: boolean;
    onClose: () => void;
    recentOrders: Order[];
    loading: boolean;
    onSelectOrder: (publicId: number) => void;
    onLoadMore?: () => void;
    hasMore?: boolean;
    isAdmin?: boolean;
}

const style = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '95%', md: '75%' },
    maxWidth: 950,
    maxHeight: '85vh',
    overflow: 'auto',
    bgcolor: 'background.paper',
    borderRadius: '12px',
    boxShadow: 24,
    p: 3,
};

const orderStateConfig: Record<OrderState, { label: string; icon: React.ReactElement; color: string }> = {
    [OrderState.ORDER_CREATED]: { label: 'Zamówienie utworzone', icon: <PendingIcon />, color: '#9e9e9e' },
    [OrderState.SCANS_ADDED]: { label: 'Skany dodane', icon: <DescriptionIcon />, color: '#2196f3' },
    [OrderState.SCANS_ACCEPTED]: { label: 'Skany zaakceptowane', icon: <CheckCircleIcon />, color: '#4caf50' },
    [OrderState.COMPLETED]: { label: 'Zakończone', icon: <DoneAllIcon />, color: '#388e3c' },
    [OrderState.CANCELLATION_REQUEST]: { label: 'Prośba o anulowanie', icon: <BlockIcon />, color: '#ff9800' },
    [OrderState.CANCELED]: { label: 'Anulowane', icon: <CancelIcon />, color: '#f44336' },
    [OrderState.APPROVED_WITHOUT_SCANS]: { label: 'Zatwierdzone bez skanów', icon: <VerifiedIcon />, color: '#9c27b0' },
    [OrderState.SCANS_ADDED_AFTER_APPROVAL]: { label: 'Skany dodane po zatwierdzeniu', icon: <PostAddIcon />, color: '#00bcd4' },
};

function ExpandableOrderRow({ order, onSelect, isAdmin }: { order: Order; onSelect: (publicId: number) => void; isAdmin?: boolean }) {
    const [open, setOpen] = useState(false);
    const theme = useTheme();

    const getDisplayName = (order: Order) => {
        return order.customeClientServiceName && order.customeClientServiceName.trim()
            ? order.customeClientServiceName
            : order.serviceName;
    };

    const formatDate = (date: Date | string) => {
        const d = typeof date === 'string' ? new Date(date) : date;
        return format(d, 'dd.MM.yyyy', { locale: pl });
    };

    const getEducationEffects = (effects: string | undefined) => {
        if (!effects) return [];
        return effects.split(';').filter((e: string) => e.trim());
    };

    const stateConfig = orderStateConfig[order.orderState] || { label: 'Nieznany', icon: <PendingIcon />, color: '#9e9e9e' };

    return (
        <>
            <TableRow
                sx={{
                    '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? '#555555' : '#f5f5f5',
                    }
                }}
            >
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{order.publicId}</TableCell>
                <TableCell>{formatDate(order.orderDateTime)}</TableCell>
                <TableCell>{getDisplayName(order)}</TableCell>
                {isAdmin && <TableCell>{order.userDisplayName || order.userEmail || '-'}</TableCell>}
                <TableCell>
                    <Tooltip title={stateConfig.label} arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: stateConfig.color }}>
                            {stateConfig.icon}
                        </Box>
                    </Tooltip>
                </TableCell>
            </TableRow>
            <TableRow
                sx={{
                    backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#fafafa',
                    '& td': {
                        padding: '0',
                        color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                    }
                }}
            >
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={isAdmin ? 6 : 5}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2, display: 'flex', flexDirection: 'column' }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Efekty kształcenia:
                                </Typography>
                                {getEducationEffects(order.educationEffects).length > 0 ? (
                                    <Box component="ol" sx={{ pl: 2, m: 0, mb: 2 }}>
                                        {getEducationEffects(order.educationEffects).map((effect: string, idx: number) => (
                                            <Box component="li" key={idx} sx={{ mb: 0.5 }}>
                                                {effect.trim()}
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Brak efektów kształcenia
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => onSelect(order.publicId)}
                                >
                                    Wczytaj efekty
                                </Button>
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function OrderSelectionModal({ open, onClose, recentOrders, loading, onSelectOrder, onLoadMore, hasMore, isAdmin }: Props) {
    const theme = useTheme();
    const ITEMS_PER_PAGE = 10;
    const DEBOUNCE_DELAY = 1000; // 1 second delay
    
    // Input filter state (real-time updates)
    const [filterOrderNumber, setFilterOrderNumber] = useState('');
    const [filterServiceNameCombined, setFilterServiceNameCombined] = useState('');
    const [filterUsername, setFilterUsername] = useState('');
    
    // Debounced filter state (used for actual filtering)
    const [debouncedFilterOrderNumber, setDebouncedFilterOrderNumber] = useState('');
    const [debouncedFilterServiceNameCombined, setDebouncedFilterServiceNameCombined] = useState('');
    const [debouncedFilterUsername, setDebouncedFilterUsername] = useState('');
    
    // Filtering state
    const [isFiltering, setIsFiltering] = useState(false);

    // Debounce effect for all filters
    useEffect(() => {
        // Check if any filter has changed from debounced values
        if (
            filterOrderNumber !== debouncedFilterOrderNumber ||
            filterServiceNameCombined !== debouncedFilterServiceNameCombined ||
            filterUsername !== debouncedFilterUsername
        ) {
            setIsFiltering(true);
        }

        const timer = setTimeout(() => {
            setDebouncedFilterOrderNumber(filterOrderNumber);
            setDebouncedFilterServiceNameCombined(filterServiceNameCombined);
            setDebouncedFilterUsername(filterUsername);
            setIsFiltering(false);
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(timer);
    }, [filterOrderNumber, filterServiceNameCombined, filterUsername, debouncedFilterOrderNumber, debouncedFilterServiceNameCombined, debouncedFilterUsername]);

    const handleRowClick = (publicId: number) => {
        onSelectOrder(publicId);
        onClose();
    };
    
    // Filter orders based on debounced search criteria
    const filteredOrders = recentOrders.filter(order => {
        const orderNumberMatch = order.publicId.toString().includes(debouncedFilterOrderNumber);
        const serviceNameMatch = 
            order.serviceName.toLowerCase().includes(debouncedFilterServiceNameCombined.toLowerCase()) ||
            order.customeClientServiceName.toLowerCase().includes(debouncedFilterServiceNameCombined.toLowerCase());
        const usernameMatch = isAdmin ? (order.userDisplayName || order.userEmail || '').toLowerCase().includes(debouncedFilterUsername.toLowerCase()) : true;
        
        return orderNumberMatch && serviceNameMatch && usernameMatch;
    });
    
    // Check if there are more orders to load
    // Show button if we have exactly a multiple of ITEMS_PER_PAGE orders and hasMore is true
    const canLoadMore = hasMore && recentOrders.length % ITEMS_PER_PAGE === 0 && recentOrders.length > 0;

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="order-selection-modal-title"
            aria-describedby="order-selection-modal-description"
        >
            <Box sx={style}>
                <Typography id="order-selection-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                    Wybierz zamówienie
                </Typography>

                {loading && recentOrders.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                        <CircularProgress />
                    </Box>
                ) : recentOrders.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                        <Typography>Brak dostępnych zamówień</Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>Filtry:</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Nr zamówienia"
                                        value={filterOrderNumber}
                                        onChange={(e) => setFilterOrderNumber(e.target.value)}
                                        placeholder="Wpisz numer..."
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Nazwa serwisu / egzaminu"
                                        value={filterServiceNameCombined}
                                        onChange={(e) => setFilterServiceNameCombined(e.target.value)}
                                        placeholder="Wpisz nazwę serwisu lub egzaminu..."
                                    />
                                </Grid>
                                {isAdmin && (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Użytkownik"
                                            value={filterUsername}
                                            onChange={(e) => setFilterUsername(e.target.value)}
                                            placeholder="Wpisz nazwę użytkownika..."
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Box>

                        <Box sx={{ position: 'relative', mb: 2 }}>
                            <TableContainer component={Paper} sx={{ mb: 2, opacity: isFiltering ? 0.5 : 1, transition: 'opacity 0.3s' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#333333' : '#f5f5f5' }}>
                                            <TableCell sx={{ width: '50px', fontWeight: 'bold' }}></TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Nr zamówienia</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Data zamówienia</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Nazwa serwisu</TableCell>
                                            {isAdmin && <TableCell sx={{ fontWeight: 'bold' }}>Użytkownik</TableCell>}
                                            <TableCell sx={{ fontWeight: 'bold', width: '60px' }}>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredOrders.length > 0 ? (
                                            filteredOrders.map((order) => (
                                                <ExpandableOrderRow 
                                                    key={order.publicId} 
                                                    order={order} 
                                                    onSelect={handleRowClick}
                                                    isAdmin={isAdmin}
                                                />
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={isAdmin ? 6 : 5} sx={{ textAlign: 'center', py: 3 }}>
                                                    <Typography color="textSecondary">Brak zamówień spełniających kryteria filtrowania</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {isFiltering && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                        borderRadius: 1,
                                        zIndex: 10
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={30} />
                                        <Typography variant="body2" sx={{ mt: 1 }}>Filtrowanie...</Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {canLoadMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => onLoadMore && onLoadMore()}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                                    Wczytaj więcej
                                </Button>
                            </Box>
                        )}

                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                    </>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={onClose} variant="outlined">
                        Anuluj
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
